import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  loading?: boolean;
  icon?: React.ReactNode;
  valuePrefix?: string;
  valueSuffix?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  changeLabel,
  loading = false,
  icon,
  valuePrefix = '',
  valueSuffix = '',
}) => {
  const getTrendIcon = () => {
    if (change === undefined || change === 0) {
      return <TrendingFlatIcon sx={{ fontSize: 16 }} />;
    }
    return change > 0 ? (
      <TrendingUpIcon sx={{ fontSize: 16 }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 16 }} />
    );
  };

  const getTrendColor = () => {
    if (change === undefined || change === 0) return 'text.secondary';
    return change > 0 ? 'success.main' : 'error.main';
  };

  const formatChange = () => {
    if (change === undefined) return '';
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="80%" height={48} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: 'primary.main', opacity: 0.7 }}>{icon}</Box>
          )}
        </Box>

        <Typography variant="h4" sx={{ mt: 1, fontWeight: 600 }}>
          {valuePrefix}{value}{valueSuffix}
        </Typography>

        {change !== undefined && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              color: getTrendColor(),
            }}
          >
            {getTrendIcon()}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatChange()}
            </Typography>
            {changeLabel && (
              <Typography variant="body2" color="text.secondary">
                {changeLabel}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
